import React, { useMemo, useState } from 'react';
import { useChatUi, useMessageContext } from '../ChatContext';
import { sourceThumbUrl } from '../api/config';
import { citedIdsInText } from '../markdown/remarkCitations';
import PageImage from './PageImage';
import FileTile from './FileTile';
import { sourceLocation } from './CitationChip';

export function SourceCard({ source, cited }) {
  const { hoveredSourceId, setHoveredSourceId, openSource } = useMessageContext();
  const active = hoveredSourceId === source.id;
  return (
    <button
      type="button"
      className={`cv-source-card${active ? ' is-active' : ''}${cited ? ' is-cited' : ''}`}
      onClick={() => openSource(source.id)}
      onMouseEnter={() => setHoveredSourceId(source.id)}
      onMouseLeave={() => setHoveredSourceId(null)}
      title={[sourceLocation(source), source.section_title || source.title].filter(Boolean).join('\n')}
    >
      {source.kind === 'file'
        ? <FileTile name={source.manual_label} className="cv-source-card__thumb" />
        : <PageImage src={sourceThumbUrl(source)} alt={sourceLocation(source)} ratio="3 / 4" className="cv-source-card__thumb" />}
      <span className="cv-source-card__meta">
        <span className="cv-source-card__top">
          <span className="cv-badge">{source.id}</span>
          <span className="cv-source-card__manual">{source.manual_label || source.manual_name}</span>
        </span>
        <span className="cv-source-card__page">
          {[source.page_label, source.printed_page].filter(Boolean).join(' · ')}
        </span>
        {(source.section || source.section_title) && (
          <span className="cv-source-card__section">{source.section_title || source.section}</span>
        )}
      </span>
    </button>
  );
}

/**
 * The pages behind an answer: cited pages first (numbered like the chips), as a horizontal strip; the
 * other consulted pages collapse behind "+N more pages consulted". Appears as soon as `sources` arrives.
 */
export default function SourcesPanel({ message }) {
  const { t } = useChatUi();
  const [showRest, setShowRest] = useState(false);
  const { sources, sourceById, citedIds, status, text } = message;
  const final = status === 'complete' || status === 'stopped' || status === 'error';

  const { cited, rest } = useMemo(() => {
    const ids = final && citedIds.length ? citedIds : citedIdsInText(text, sourceById);
    const citedList = ids.map((id) => sourceById[id]).filter(Boolean);
    const citedSet = new Set(citedList.map((s) => s.id));
    return { cited: citedList, rest: sources.filter((s) => !citedSet.has(s.id)) };
  }, [final, citedIds, text, sourceById, sources]);

  if (!sources.length) return null;
  const restOpen = showRest || cited.length === 0;

  return (
    <section className="cv-sources" aria-label={t('Sources')}>
      <div className="cv-sources__head">
        <span className="cv-section-label">
          {cited.length ? t('Sources') : t('Pages consulted')}
          <span className="cv-count">{cited.length || sources.length}</span>
        </span>
        {cited.length > 0 && rest.length > 0 && (
          <button type="button" className="cv-link-btn" onClick={() => setShowRest((v) => !v)} aria-expanded={showRest}>
            {showRest ? t('Hide other pages') : `+${rest.length} ${t('more pages consulted')}`}
          </button>
        )}
      </div>
      <div className="cv-sources__strip">
        {cited.map((s) => <SourceCard key={s.id} source={s} cited />)}
        {restOpen && rest.map((s) => <SourceCard key={s.id} source={s} />)}
      </div>
    </section>
  );
}
